/**
 * Vendored from AudioCN (https://github.com/audiocn/ui), hand-copied at
 * commit 42f863ec333c6912c421d1e9d6d7ce7850ce2680.
 * Source file: lib/audio/time.ts
 * License: MIT, Copyright (c) 2026 OrcDev. See components/ui/audiocn/LICENSE.md.
 * Local changes:
 *   - New `decimals` option for the player readout's `0:00.62` form (§01.9).
 *     Without it the output is unchanged.
 */
const SECONDS_PER_MINUTE = 60;
const SECONDS_PER_HOUR = 3600;
const MINUS_SIGN = "−";

export interface FormatTimeOptions {
  /** Prefix with "−", for time remaining. */
  remaining?: boolean;
  /** Always show hours. */
  hours?: boolean;
  /**
   * Digits after the seconds, rounded: 2 gives `0:00.62`. Default 0, which
   * floors to whole seconds as before.
   */
  decimals?: number;
}

const pad = (value: number): string => String(value).padStart(2, "0");

/** Formats seconds as `m:ss`, or `h:mm:ss` from one hour. */
export const formatTime = (
  seconds: number,
  options: FormatTimeOptions = {}
): string => {
  const decimals = options.decimals ?? 0;
  const scale = 10 ** decimals;
  const safe = Number.isFinite(seconds) ? Math.max(0, seconds) : 0;
  const scaled = decimals > 0 ? Math.round(safe * scale) : Math.floor(safe);
  const total = Math.floor(scaled / scale);
  const hours = Math.floor(total / SECONDS_PER_HOUR);
  const minutes = Math.floor((total % SECONDS_PER_HOUR) / SECONDS_PER_MINUTE);
  const secs = total % SECONDS_PER_MINUTE;
  const prefix = options.remaining ? MINUS_SIGN : "";
  const fraction =
    decimals > 0 ? `.${String(scaled % scale).padStart(decimals, "0")}` : "";

  if (hours > 0 || options.hours) {
    return `${prefix}${hours}:${pad(minutes)}:${pad(secs)}${fraction}`;
  }
  return `${prefix}${minutes}:${pad(secs)}${fraction}`;
};
